import {
  ProtocolError,
  concatBytes,
  equalBytes,
  hexToBytes,
  type Direction,
  type PhysicalChannel,
  type SequenceReplayWindow,
} from '@spectrabus/protocol';

export const PHYSICAL_FRAME_AUTH_TAG_BYTES = 16;
export const MAX_AUTHENTICATED_FRAME_PAYLOAD_BYTES = 1024 * 1024;

const FRAME_AUTH_MAGIC = 0x53424641;
const FRAME_AUTH_VERSION = 1;
const FIXED_HEADER_BYTES = 40;
const MAX_LABEL_BYTES = 64;
const FRAME_AUTH_KEY_BYTES = 32;
const FRAME_AUTH_DOMAIN = new TextEncoder().encode('SpectraBus/1/physical-frame-auth');

const DIRECTION_CODE: Readonly<Record<Direction, number>> = {
  'sender-to-receiver': 1,
  'receiver-to-sender': 2,
};

const CHANNEL_CODE: Readonly<Record<PhysicalChannel, number>> = {
  optical: 1,
  acoustic: 2,
};

export interface PhysicalFrameBinding {
  readonly sessionId: string;
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly sequence: number;
  readonly symbolId: number;
  readonly profileId: string;
  readonly contextId: string;
}

export interface PhysicalFrameExpectation {
  readonly sessionId: string;
  readonly direction: Direction;
  readonly channel: PhysicalChannel;
  readonly profileId: string;
  readonly contextId: string;
  readonly sequence?: number;
  readonly symbolId?: number;
  readonly replayWindow?: SequenceReplayWindow;
}

export interface OpenedPhysicalFrame {
  readonly binding: PhysicalFrameBinding;
  readonly payload: Uint8Array;
}

export type PhysicalFrameAuthFailure =
  | 'malformed-envelope'
  | 'authentication-tag'
  | 'binding-mismatch';

export class PhysicalFrameAuthError extends ProtocolError {
  public readonly failure: PhysicalFrameAuthFailure;

  public constructor(failure: PhysicalFrameAuthFailure, message: string) {
    super('PHYSICAL_FRAME_AUTH_FAILURE', message);
    this.name = 'PhysicalFrameAuthError';
    this.failure = failure;
  }
}

interface ParsedEnvelope extends OpenedPhysicalFrame {
  readonly authenticatedBytes: Uint8Array;
  readonly tag: Uint8Array;
}

function subtle(): SubtleCrypto {
  if (globalThis.crypto?.subtle === undefined) {
    throw new ProtocolError('WEB_CRYPTO_UNAVAILABLE', 'Web Crypto is required for SpectraBus.');
  }
  return globalThis.crypto.subtle;
}

function ownedBuffer(bytes: Uint8Array): ArrayBuffer {
  const owned = new Uint8Array(bytes.length);
  owned.set(bytes);
  return owned.buffer;
}

function malformed(message: string): PhysicalFrameAuthError {
  return new PhysicalFrameAuthError('malformed-envelope', message);
}

function validateUint32(value: number, field: string): void {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
    throw new ProtocolError(
      'PHYSICAL_FRAME_FIELD_BOUNDS',
      `${field} must be an unsigned 32-bit integer.`,
    );
  }
}

function encodeLabel(value: string, field: string): Uint8Array {
  const bytes = new TextEncoder().encode(value);
  if (bytes.length < 1 || bytes.length > MAX_LABEL_BYTES) {
    throw new ProtocolError(
      'PHYSICAL_FRAME_LABEL_BOUNDS',
      `${field} must contain between one and ${MAX_LABEL_BYTES} UTF-8 bytes.`,
    );
  }
  return bytes;
}

function decodeLabel(bytes: Uint8Array, field: string): string {
  try {
    const value = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    if (new TextEncoder().encode(value).length !== bytes.length) {
      throw malformed(`${field} does not use canonical UTF-8.`);
    }
    return value;
  } catch (error) {
    if (error instanceof PhysicalFrameAuthError) throw error;
    throw malformed(`${field} is not valid UTF-8.`);
  }
}

function validateSessionId(sessionId: string): Uint8Array {
  if (!/^[\da-f]{32}$/iu.test(sessionId)) {
    throw new ProtocolError(
      'PHYSICAL_FRAME_SESSION_ID',
      'Physical-frame session identifiers must contain exactly 128 bits of hexadecimal data.',
    );
  }
  return hexToBytes(sessionId);
}

function validateAuthenticationKey(key: Uint8Array): void {
  if (key.length !== FRAME_AUTH_KEY_BYTES) {
    throw new ProtocolError(
      'PHYSICAL_FRAME_AUTH_KEY_LENGTH',
      'Physical-frame authentication keys must contain exactly 256 bits.',
    );
  }
}

function validateBinding(binding: PhysicalFrameBinding): {
  readonly sessionId: Uint8Array;
  readonly profileId: Uint8Array;
  readonly contextId: Uint8Array;
} {
  validateUint32(binding.sequence, 'Physical-frame sequence');
  validateUint32(binding.symbolId, 'Physical-frame symbol identifier');
  return {
    sessionId: validateSessionId(binding.sessionId),
    profileId: encodeLabel(binding.profileId, 'Physical-frame profile identifier'),
    contextId: encodeLabel(binding.contextId, 'Physical-frame context identifier'),
  };
}

async function hmac(key: Uint8Array, authenticatedBytes: Uint8Array): Promise<Uint8Array> {
  validateAuthenticationKey(key);
  const cryptoKey = await subtle().importKey(
    'raw',
    ownedBuffer(key),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return new Uint8Array(
    await subtle().sign(
      'HMAC',
      cryptoKey,
      ownedBuffer(concatBytes(FRAME_AUTH_DOMAIN, authenticatedBytes)),
    ),
  );
}

export async function derivePhysicalFrameAuthKey(
  objectRootKey: Uint8Array,
  sessionId: string,
): Promise<Uint8Array> {
  validateAuthenticationKey(objectRootKey);
  const sessionBytes = validateSessionId(sessionId);
  const material = await subtle().importKey('raw', ownedBuffer(objectRootKey), 'HKDF', false, [
    'deriveBits',
  ]);
  return new Uint8Array(
    await subtle().deriveBits(
      {
        name: 'HKDF',
        hash: 'SHA-256',
        salt: ownedBuffer(sessionBytes),
        info: ownedBuffer(FRAME_AUTH_DOMAIN),
      },
      material,
      256,
    ),
  );
}

export async function sealPhysicalFrameEnvelope(
  payload: Uint8Array,
  key: Uint8Array,
  binding: PhysicalFrameBinding,
): Promise<Uint8Array> {
  if (payload.length > MAX_AUTHENTICATED_FRAME_PAYLOAD_BYTES) {
    throw new ProtocolError(
      'PHYSICAL_FRAME_PAYLOAD_BOUNDS',
      'Physical-frame payload exceeds the authenticated envelope limit.',
    );
  }
  const encoded = validateBinding(binding);
  const authenticatedBytes = new Uint8Array(
    FIXED_HEADER_BYTES + encoded.profileId.length + encoded.contextId.length + payload.length,
  );
  const view = new DataView(authenticatedBytes.buffer);
  view.setUint32(0, FRAME_AUTH_MAGIC);
  view.setUint8(4, FRAME_AUTH_VERSION);
  view.setUint8(5, DIRECTION_CODE[binding.direction]);
  view.setUint8(6, CHANNEL_CODE[binding.channel]);
  view.setUint8(7, encoded.profileId.length);
  view.setUint8(8, encoded.contextId.length);
  authenticatedBytes.set(encoded.sessionId, 12);
  view.setUint32(28, binding.sequence);
  view.setUint32(32, binding.symbolId);
  view.setUint32(36, payload.length);

  let offset = FIXED_HEADER_BYTES;
  authenticatedBytes.set(encoded.profileId, offset);
  offset += encoded.profileId.length;
  authenticatedBytes.set(encoded.contextId, offset);
  offset += encoded.contextId.length;
  authenticatedBytes.set(payload, offset);

  const fullTag = await hmac(key, authenticatedBytes);
  return concatBytes(authenticatedBytes, fullTag.subarray(0, PHYSICAL_FRAME_AUTH_TAG_BYTES));
}

function parseEnvelope(envelope: Uint8Array): ParsedEnvelope {
  if (envelope.length < FIXED_HEADER_BYTES + PHYSICAL_FRAME_AUTH_TAG_BYTES) {
    throw malformed('Physical-frame authentication envelope is truncated.');
  }
  const bytes = envelope.slice();
  const view = new DataView(bytes.buffer);
  if (view.getUint32(0) !== FRAME_AUTH_MAGIC) {
    throw malformed('Physical-frame authentication envelope has invalid magic.');
  }
  if (view.getUint8(4) !== FRAME_AUTH_VERSION) {
    throw malformed('Physical-frame authentication envelope uses an unsupported version.');
  }
  if (view.getUint8(9) !== 0 || view.getUint8(10) !== 0 || view.getUint8(11) !== 0) {
    throw malformed('Physical-frame authentication envelope has non-zero reserved bytes.');
  }

  const directionCode = view.getUint8(5);
  const channelCode = view.getUint8(6);
  const direction =
    directionCode === 1
      ? 'sender-to-receiver'
      : directionCode === 2
        ? 'receiver-to-sender'
        : null;
  const channel = channelCode === 1 ? 'optical' : channelCode === 2 ? 'acoustic' : null;
  if (direction === null || channel === null) {
    throw malformed('Physical-frame authentication envelope contains an unknown direction or channel.');
  }

  const profileLength = view.getUint8(7);
  const contextLength = view.getUint8(8);
  const payloadLength = view.getUint32(36);
  if (
    profileLength < 1 ||
    profileLength > MAX_LABEL_BYTES ||
    contextLength < 1 ||
    contextLength > MAX_LABEL_BYTES ||
    payloadLength > MAX_AUTHENTICATED_FRAME_PAYLOAD_BYTES
  ) {
    throw malformed('Physical-frame authentication envelope exceeds negotiated bounds.');
  }
  const authenticatedLength = FIXED_HEADER_BYTES + profileLength + contextLength + payloadLength;
  if (bytes.length !== authenticatedLength + PHYSICAL_FRAME_AUTH_TAG_BYTES) {
    throw malformed('Physical-frame authentication envelope length does not match its header.');
  }

  let offset = FIXED_HEADER_BYTES;
  const profileId = decodeLabel(bytes.subarray(offset, offset + profileLength), 'Profile identifier');
  offset += profileLength;
  const contextId = decodeLabel(bytes.subarray(offset, offset + contextLength), 'Context identifier');
  offset += contextLength;

  const binding: PhysicalFrameBinding = {
    sessionId: Array.from(bytes.subarray(12, 28), (value) =>
      value.toString(16).padStart(2, '0'),
    ).join(''),
    direction,
    channel,
    sequence: view.getUint32(28),
    symbolId: view.getUint32(32),
    profileId,
    contextId,
  };
  return {
    binding,
    payload: bytes.slice(offset, offset + payloadLength),
    authenticatedBytes: bytes.slice(0, authenticatedLength),
    tag: bytes.slice(authenticatedLength),
  };
}

function matchesExpectation(
  binding: PhysicalFrameBinding,
  expectation: PhysicalFrameExpectation,
): boolean {
  const expectedSessionId = validateSessionId(expectation.sessionId);
  const actualSessionId = validateSessionId(binding.sessionId);
  encodeLabel(expectation.profileId, 'Expected physical-frame profile identifier');
  encodeLabel(expectation.contextId, 'Expected physical-frame context identifier');
  if (expectation.sequence !== undefined) {
    validateUint32(expectation.sequence, 'Expected physical-frame sequence');
  }
  if (expectation.symbolId !== undefined) {
    validateUint32(expectation.symbolId, 'Expected physical-frame symbol identifier');
  }
  return (
    equalBytes(actualSessionId, expectedSessionId) &&
    binding.direction === expectation.direction &&
    binding.channel === expectation.channel &&
    binding.profileId === expectation.profileId &&
    binding.contextId === expectation.contextId &&
    (expectation.sequence === undefined || binding.sequence === expectation.sequence) &&
    (expectation.symbolId === undefined || binding.symbolId === expectation.symbolId)
  );
}

export async function openPhysicalFrameEnvelope(
  envelope: Uint8Array,
  key: Uint8Array,
  expectation: PhysicalFrameExpectation,
): Promise<OpenedPhysicalFrame> {
  const parsed = parseEnvelope(envelope);
  const fullTag = await hmac(key, parsed.authenticatedBytes);
  const expectedTag = fullTag.subarray(0, PHYSICAL_FRAME_AUTH_TAG_BYTES);
  if (!equalBytes(parsed.tag, expectedTag)) {
    throw new PhysicalFrameAuthError(
      'authentication-tag',
      'Physical-frame HMAC-SHA-256 verification failed.',
    );
  }
  if (!matchesExpectation(parsed.binding, expectation)) {
    throw new PhysicalFrameAuthError(
      'binding-mismatch',
      'Authenticated physical frame does not match the expected session or transport context.',
    );
  }
  expectation.replayWindow?.accept(parsed.binding.sequence);
  return { binding: parsed.binding, payload: parsed.payload };
}
